import { NavLink, useLocation } from 'react-router-dom';
import { makeStyles, tokens } from '@fluentui/react-components';
import {
  People24Regular,
  Building24Regular,
  BuildingMultiple24Regular,
  Flow24Regular,
  Mail24Regular,
  Form24Regular,
  Document24Regular,
  Shield24Regular,
  ClipboardCheckmark24Regular,
  Color24Regular,
  Key24Regular,
  PersonAvailable24Regular,
  Laptop24Regular,
  Certificate24Regular,
  ArrowLeft24Regular,
  ChartMultiple24Regular,
  DeviceMeetingRoom24Regular,
  History24Regular,
  HeartPulse24Regular,
  PersonProhibited24Regular,
  PersonGuest24Regular,
  TaskListSquareLtr24Regular,
  PersonDelete24Regular,
  ArrowSync24Regular,
} from '@fluentui/react-icons';
import { useTranslation } from 'react-i18next';
import { useEffectiveTenant } from '@shared/hooks';

const useStyles = makeStyles({
  nav: {
    display: 'flex',
    flexDirection: 'column',
    padding: tokens.spacingVerticalM,
    gap: tokens.spacingVerticalXS,
  },
  section: {
    marginBottom: tokens.spacingVerticalM,
  },
  sectionTitle: {
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground3,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalM}`,
  },
  link: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalM,
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalM}`,
    borderRadius: tokens.borderRadiusMedium,
    textDecoration: 'none',
    color: tokens.colorNeutralForegroundOnBrand,
    fontSize: tokens.fontSizeBase300,
    transition: 'background-color 0.15s ease',
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.1)',
    },
  },
  linkActive: {
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    fontWeight: tokens.fontWeightSemibold,
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.25)',
    },
  },
  backLink: {
    borderTop: '1px solid rgba(255, 255, 255, 0.2)',
    marginTop: 'auto',
    paddingTop: tokens.spacingVerticalM,
  },
  icon: {
    fontSize: '20px',
    flexShrink: 0,
  },
});

interface NavItemProps {
  to: string;
  icon: React.ReactNode;
  label: string;
  end?: boolean; // If true, only match exact path (like React Router's "end" prop)
}

function NavItem({ to, icon, label, end = false }: NavItemProps) {
  const styles = useStyles();
  const location = useLocation();
  const isActive = end
    ? location.pathname === to
    : location.pathname === to || location.pathname.startsWith(`${to}/`);

  return (
    <NavLink to={to} className={`${styles.link} ${isActive ? styles.linkActive : ''}`}>
      <span className={styles.icon}>{icon}</span>
      <span>{label}</span>
    </NavLink>
  );
}

export function AdminBackendNavMenu() {
  const styles = useStyles();
  const { t } = useTranslation('admin');
  const { isPlatformAdmin } = useEffectiveTenant();

  return (
    <nav className={styles.nav}>
      {/* Analytics Dashboard - matches Blazor @page "/admin" */}
      <div className={styles.section}>
        <NavItem to="/admin" icon={<ChartMultiple24Regular />} label={t('analytics.visitorAnalytics')} end />
      </div>

      <div className={styles.section}>
        <div className={styles.sectionTitle}>{t('usersSectionTitle')}</div>
        <NavItem to="/admin/users" icon={<People24Regular />} label={t('userManagement')} />
        <NavItem to="/admin/tenants" icon={<Building24Regular />} label={t('tenantsNavLabel')} />
        {/* Phase 51 plan 51-10 (D-10): cross-tenant list + create, platform_admin only */}
        {isPlatformAdmin && (
          <NavItem to="/admin/all-tenants" icon={<BuildingMultiple24Regular />} label={t('tenantSettings.allTenantsNavLabel')} />
        )}
      </div>

      <div className={styles.section}>
        <div className={styles.sectionTitle}>{t('configuration')}</div>
        <NavItem to="/admin/workflows" icon={<Flow24Regular />} label={t('workflows')} />
        {/* Phase 32 plan 32-01: operator instance-list surface (WF-17/WF-20) */}
        <NavItem to="/admin/workflow-instances" icon={<TaskListSquareLtr24Regular />} label={t('workflowInstances.title')} />
        {/* Route: /admin/mail-templates - matches Blazor @page "/admin/mail-templates" */}
        <NavItem to="/admin/mail-templates" icon={<Mail24Regular />} label={t('mailTemplates')} />
        {/* Phase 20 plan 20-06: admin VisitorTypes surface (mirrors Locations) */}
        <NavItem to="/admin/visitor-types" icon={<PersonGuest24Regular />} label={t('visitorTypes.navLabel')} />
        {/* Route: /admin/form-definitions - matches Blazor @page "/admin/form-definitions" */}
        <NavItem to="/admin/form-definitions" icon={<Form24Regular />} label={t('formDefinitions')} />
        <NavItem to="/admin/documents" icon={<Document24Regular />} label={t('documentsNavLabel')} />
      </div>

      <div className={styles.section}>
        <div className={styles.sectionTitle}>{t('security')}</div>
        {/* Route: /admin/accessControl - matches Blazor @page "/admin/accessControl" */}
        <NavItem to="/admin/accessControl" icon={<Shield24Regular />} label={t('accessControlNavLabel')} />
        {/* Route: /admin/safetyTrainings - matches Blazor @page "/admin/safetyTrainings" */}
        <NavItem to="/admin/safetyTrainings" icon={<Certificate24Regular />} label={t('safetyTrainingNavLabel')} />
        <NavItem to="/admin/banlist" icon={<PersonProhibited24Regular />} label={t('banlist.navLabel')} />
        {/* Route: /admin/wachbuch - Phase 18 plan 18-16 (Variante D matrix admin) */}
        <NavItem to="/admin/logbook" icon={<ClipboardCheckmark24Regular />} label={t('nav.wachbuch')} />
        {/* Phase 47 plan 47-02: retention-eligibility admin list (RET-01, D-11) */}
        <NavItem to="/admin/data-retention" icon={<PersonDelete24Regular />} label={t('nav.dataRetention')} />
      </div>

      <div className={styles.section}>
        <div className={styles.sectionTitle}>{t('operations')}</div>
        {/* Route: /admin/rooms - matches Blazor @page "/admin/rooms" */}
        <NavItem to="/admin/rooms" icon={<DeviceMeetingRoom24Regular />} label={t('rooms.title')} />
        <NavItem to="/admin/visitors" icon={<PersonAvailable24Regular />} label={t('visitorsNavLabel')} />
        {/* Route: /admin/issuance/items - matches Blazor @page "/admin/issuance/items" */}
        <NavItem to="/admin/issuance/items" icon={<Key24Regular />} label={t('issuanceItems')} />
        {/* Route: /admin/issuance/persons - matches Blazor @page "/admin/issuance/persons" */}
        <NavItem to="/admin/issuance/persons" icon={<History24Regular />} label={t('issuancePersons')} />
        {/* Route: /admin/kiosk - matches Blazor @page "/admin/kiosk" (singular) */}
        <NavItem to="/admin/kiosk" icon={<Laptop24Regular />} label={t('kiosk')} />
      </div>

      <div className={styles.section}>
        <div className={styles.sectionTitle}>{t('system')}</div>
        <NavItem to="/admin/health" icon={<HeartPulse24Regular />} label={t('health.navTitle')} />
        {/* Quick 261015-bw9: Transconnect request log (admin only) */}
        <NavItem to="/admin/transconnect" icon={<ArrowSync24Regular />} label={t('nav.transconnect')} />
        <NavItem to="/admin/settings/appearance" icon={<Color24Regular />} label={t('appearance')} />
      </div>

      <div className={styles.backLink}>
        <NavLink to="/" className={styles.link}>
          <span className={styles.icon}><ArrowLeft24Regular /></span>
          <span>{t('backToApp')}</span>
        </NavLink>
      </div>
    </nav>
  );
}
